import { useEffect, useState } from 'react';
import { useApiClient } from './useApiClient';
import { PlatformQuotationsParameters } from 'src/motornet';

type VehicleQuotationParams = PlatformQuotationsParameters & {
	vehicle_id: number | null;
	durata: number[];
	km_annui: number[];
	noleggiatore: string[];
};

const totalKmOptions = [
	{ value: 10000, label: '10.000' },
	{ value: 15000, label: '15.000' },
	{ value: 20000, label: '20.000' },
	{ value: 25000, label: '25.000' },
	{ value: 30000, label: '30.000' },
];

const rentalDurationOptions = [
	{ value: 24, label: '24' },
	{ value: 36, label: '36' },
	{ value: 48, label: '48' },
	{ value: 60, label: '60' },
];

const formatNumberLocale = (value) => {
	return value.toLocaleString('it-IT', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	});
};

const generateTotalKmOptions = (values) => {
	if (!values || !values.length) {
		return totalKmOptions.slice();
	}

	return values.map((value) => ({
		value,
		label: formatNumberLocale(value),
	}));
};

const generateRentalDurationOptions = (values) => {
	if (!values || !values.length) {
		return rentalDurationOptions.slice();
	}

	return values.map((value) => ({
		value,
		label: value,
	}));
};

const makeLimit = (): number => {
	return 1;
};

const makeDefaultParams = () => ({
	totalKmOptions: totalKmOptions.slice(),
	rentalDurationOptions: rentalDurationOptions.slice(),
	noleggiatore: [],
	showLimit: makeLimit(),
});

export function useQuickoQuotationParams(
	vehicleCode: string | number | null,
	marca?: string | null,
	enabled?: boolean
) {
	const [loadingState, setIsLoading] = useState(false);
	const { apiClient } = useApiClient();

	const [params, setParams] = useState<{
		totalKmOptions: typeof totalKmOptions;
		rentalDurationOptions: typeof rentalDurationOptions;
		noleggiatore: string[];
		showLimit: number;
	}>(makeDefaultParams());

	const [settledVehicleCode, setSettledVehicleCode] = useState<
		string | number | null | undefined
	>(undefined);

	const doFetch = async (vehicleCode: string | number | null) => {
		try {
			if (!vehicleCode) {
				setParams(makeDefaultParams());
				return;
			}

			setIsLoading(true);

			const apiResponse: VehicleQuotationParams =
				await apiClient.motornetControllerService.getQuotationsParameters(
					String(vehicleCode)
				);

			if (!apiResponse) {
				setParams(makeDefaultParams());
				return;
			}

			setParams({
				totalKmOptions: generateTotalKmOptions(apiResponse.km_annui || []),
				rentalDurationOptions: generateRentalDurationOptions(
					apiResponse.durata || []
				),
				noleggiatore: apiResponse.noleggiatore || [],
				showLimit: makeLimit(),
			});
		} catch (error) {
			console.warn(error);
			setParams(makeDefaultParams());
		} finally {
			setIsLoading(false);
			setSettledVehicleCode(vehicleCode);
		}
	};

	useEffect(() => {
		enabled && doFetch(vehicleCode);
	}, [vehicleCode, enabled]);

	return {
		isLoadingParams: loadingState,
		quotationParams: params,
		paramsVehicleCode: settledVehicleCode,
	};
}
